import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDateTimeWithoutSec, formattedDate, formattedDateTypeTwo, getCurrentDateTime1 } from "../../../../Common/helper/helpar";
import { getCurrentDateTime } from "../../../../Common/helper/helpar";

export const TonnageCompositionExport = ({ data, fromDate, toDate }) => {
  const currentDateTime = getCurrentDateTime1();
  const reportTitle = `Tonnage Composition Report as on ` + `(${formattedDateTypeTwo(fromDate)} to ${formattedDateTypeTwo(toDate)})`;
  const topHeaderText = `Tonnage Composition Report is generated on (${currentDateTime})`;

  const sum = (field) =>
    data.reduce((acc, curr) => acc + (Number(curr[field]) || 0), 0);
  
  /* ------------------ EXCEL DOWNLOAD ------------------ */
  const downloadAsExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Tonnage Report");
    const boldFont = { bold: true };

    const now = new Date();

    // Clear any existing rows (optional)
    worksheet.addRow([]);

    // First Heading: Title + Generated On
    worksheet.addRow([topHeaderText]);
    worksheet.mergeCells(2, 1, 2, 13); // merge across 13 columns
    worksheet.getCell("A2").font = { bold: true, size: 14 };
    worksheet.getCell("A2").alignment = { horizontal: "center" };

    // Second Heading: Report Date Range
    worksheet.addRow([reportTitle]);
    worksheet.mergeCells(3, 1, 3, 13); // merge across 13 columns
    worksheet.getCell("A3").font = { bold: true, size: 12 };
    worksheet.getCell("A3").alignment = { horizontal: "center" };

    // Add an empty row before table header
    worksheet.addRow([]);

    // Header
    const header = [
      "S.No",
      "Date",
      "TS/SC TP",
      "SAT Trips",
      "Secondary",
      "Others",
      "Estimated Tonnage",
      "HKL",
      "RCV",
      "PVT",
      "Est MT",
      "Actual MT",
      "Variance",
    ];
    const headerRow = worksheet.addRow(header);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };

      // RED COLOR
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F6B1B1" },
      };

      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });
    // Data
    data.forEach((item, index) => {
      worksheet.addRow([
        index + 1,
        formattedDate(item.Date?.split("T")[0]),
        item.TransferStationName || "N/A",
        // item["TS/SC TP"],
        item["SAT Trips"] || 0,
        item["Secondary"] || 0,
        item["Others"] || 0,
        item["Estimated Tonnage"] || 0,
        item["HKL"] || 0,
        item["RCV"] || 0,
        item["PVT Veh"] || 0,
        item["EstimatedWeight"] || 0,
        item["Actual"] || 0,
        item["Variance"] || 0,
      ]).eachCell((cell) => {
        cell.alignment = { horizontal: "center" };
      });
    });

    // Total row
    worksheet.addRow([
      "",
      "Total",
      "",
      sum("SAT Trips"),
      sum("Secondary"),
      sum("Others"),
      sum("Estimated Tonnage"),
      sum("HKL"),
      sum("RCV"),
      sum("PVT Veh"),
      sum("EstimatedWeight"),
      sum("Actual"),
      sum("Variance"),
    ]).eachCell((cell) => {
      cell.font = boldFont;
      cell.alignment = { horizontal: "center" };
    });

    worksheet.columns = Array(13).fill({ width: 12 });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      "TonnageCompositionReport.xlsx"
    );
  };

  /* ------------------ PDF DOWNLOAD ------------------ */
  const downloadAsPdf = () => {
    const doc = new jsPDF("l", "mm", "a4"); // landscape
    const styledText = (text, x, y, size, options = {}) => {
        const originalSize = doc.getFontSize();
        doc.setFontSize(size);
        doc.text(text, x, y, options);
        doc.setFontSize(originalSize); // Reverts back automatically
    };
    const pageWidth = doc.internal.pageSize.width;
    const titleX = pageWidth / 2;
    styledText(topHeaderText, titleX, 15, 12, { align: "center" });
    styledText(reportTitle, titleX, 24, 14, { align: "center" });

    const headingColor = "#1F4E78"; // blue color for headings

    // First heading: Report Title + Generated On


    // Second heading: Report Date Range

    // Table columns
    const columns = [
      "S.No",
      "Date",
      "TS/SC TP",
      "SAT Trips",
      "Secondary",
      "Others",
      "Estimated Tonnage",
      "HKL",
      "RCV",
      "PVT",
      "Est MT",
      "Actual MT",
      "Variance",
    ];

    // Table rows
    const rows = data.map((item, index) => [
      index + 1,
      item.Date?.split("T")[0],
      item.TransferStationName || "N/A",
      item["SAT Trips"] || 0,
      item["Secondary"] || 0,
      item["Others"] || 0,
      item["Estimated Tonnage"] || 0,
      item["HKL"] || 0,
      item["RCV"] || 0,
      item["PVT Veh"] || 0,
      item["EstimatedWeight"] || 0,
      item["Actual"] || 0,
      item["Variance"] || 0,
    ]);

    // Total row
    rows.push([
      "",
      "Total",
      "",
      sum("SAT Trips"),
      sum("Secondary"),
      sum("Others"),
      sum("Estimated Tonnage"),
      sum("HKL"),
      sum("RCV"),
      sum("PVT Veh"),
      sum("EstimatedWeight"),
      sum("Actual"),
      sum("Variance"),
    ]);

    // AutoTable with custom header colors
    autoTable(doc, {
      head: [columns],
      body: rows,
      startY: 28, // start below headings
      styles: { fontSize: 8, halign: "center" },
      headStyles: {
        fillColor: [0, 102, 204], // light blue header background
        textColor: 255,
        fontStyle: "bold",
      },
      footStyles: {
        fillColor: "#F2F2F2", // grayish background for total row
        textColor: 0,
        fontStyle: "bold",
      },
      didParseCell: (data) => {
        // Make total row bold and centered
        if (data.row.index === rows.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.halign = "center";
        }
      },
    });

    doc.save("TonnageCompositionReport.pdf");
  };


  return (
    <>
      <button className="btn btn-excel me-2" onClick={downloadAsPdf}>
        <i className="fa-solid fa-file-pdf me-1"></i> PDF
      </button>

      <button className="btn btn-excel" onClick={downloadAsExcel}>
        <i className="fa-solid fa-file-excel me-1"></i> Excel
      </button>
    </>
  );
};
